import MonthlyBreakdown from "@/domain/MonthlyBreakdown";
import ProfitBreakdown from "@/domain/ProfitBreakdown";


export function calculateMetrics(propertyRecord) {
    const rentalAfterExpenses = calculateRentalAfterExpenses(propertyRecord, 1);
    const mortgagePayment = calculateMortgagePayment(propertyRecord);
    const monthlyProfit = rentalAfterExpenses - mortgagePayment;
    if (!propertyRecord.PropertyAnalysis) {
        propertyRecord.PropertyAnalysis = {};
    }

    const strProfit = calculateShortTermRentalProfit(propertyRecord, 1);

    propertyRecord.PropertyAnalysis.RentalAfterExpenses = rentalAfterExpenses;
    propertyRecord.PropertyAnalysis.MortgagePayment = mortgagePayment;
    propertyRecord.PropertyAnalysis.MonthlyProfit = monthlyProfit;
    propertyRecord.PropertyAnalysis.MonthlyProfitWithPropMgmt = (rentalAfterExpenses - mortgagePayment - (propertyRecord.RentalPerMonth * .08));
    propertyRecord.PropertyAnalysis.ROI = calculateRoi(propertyRecord, mortgagePayment, false);
    propertyRecord.PropertyAnalysis.ROIWithAppreciation = calculateRoi(propertyRecord, mortgagePayment, true);
    propertyRecord.PropertyAnalysis.STRMonthlyIncome = strProfit.monthlyIncome;
    propertyRecord.PropertyAnalysis.STRMonthlyProfitWithMgmt = strProfit.profit - mortgagePayment;
    propertyRecord.PropertyAnalysis.STRROI = calculateSTRRoi(propertyRecord, mortgagePayment, false);
    propertyRecord.PropertyAnalysis.STRROIWithAppreciation = calculateSTRRoi(propertyRecord, mortgagePayment, true);
    return {
        RentalAfterExpenses:       rentalAfterExpenses,
        MortgagePayment:           mortgagePayment,
        MonthlyProfit:             monthlyProfit,
        MonthlyProfitWithPropMgmt: (rentalAfterExpenses - mortgagePayment - (propertyRecord.RentalPerMonth * .08)),
        ROI:                       propertyRecord.PropertyAnalysis.ROI,
        ROIWithAppreciation:       propertyRecord.PropertyAnalysis.ROIWithAppreciation,
        STRMonthlyIncome:          strProfit.monthlyIncome,
        STRMonthlyProfitWithMgmt:  strProfit.profit  - mortgagePayment
    }
}

function calculateShortTermRentalProfit(propertyRecord, year) {
    let dailyRate = propertyRecord.STRAverageDailyRate;
    let annualIncrease = propertyRecord.STRAnnualIncreasePct / 100;
    for (let i = 2; i < year; i++ ){
        dailyRate = dailyRate + (dailyRate * annualIncrease)
    }

    const daysOccupied = Math.round(30 * (propertyRecord.STROccupancyPct / 100));
    const monthlyIncome =  dailyRate * daysOccupied;
    const propMgmtFees = monthlyIncome * (propertyRecord.STRPropertyMgmtPct / 100);
    let profit = monthlyIncome - propMgmtFees;


    if (propertyRecord.HoaAnnual > 0) {
        profit -= (propertyRecord.HoaAnnual / 12)
    }

    if (propertyRecord.InsuranceAnnual > 0) {
        profit -= (propertyRecord.InsuranceAnnual / 12)
    }

    if (propertyRecord.TaxesAnnual > 0) {
        profit -= (propertyRecord.TaxesAnnual / 12)
    }

    if (propertyRecord.RepairCostsAnnual > 0) {
        profit -= (propertyRecord.RepairCostsAnnual / 12)
    }

    if (propertyRecord.UtilitiesAnnual > 0) {
        profit -= (propertyRecord.UtilitiesAnnual / 12)
    }

    return  {
      monthlyIncome: monthlyIncome,
      profit: profit
    };
}

function calculateRentalAfterExpenses(propertyRecord, year) {
    const monthlyBreakdown = new MonthlyBreakdown()
    calculateMonthlyBreakdown(monthlyBreakdown, propertyRecord, year, true)
    return monthlyBreakdown.profitExclMortgage
}

export function calculateMonthlyBreakdown(monthlyBreakdown, propertyRecord, year, includeAppreciation) {
    let rent = propertyRecord.RentalPerMonth;
    if (includeAppreciation) {
        let annualIncrease = propertyRecord.RentalAppreciationPctAnnual / 100;
        for (let i = 1; i < year; i++ ){
            rent = rent + (rent * annualIncrease)
        }
    }

    monthlyBreakdown.monthlyRent = rent
    monthlyBreakdown.hoa = propertyRecord.HoaAnnual / 12
    monthlyBreakdown.insurance = propertyRecord.InsuranceAnnual / 12
    monthlyBreakdown.taxes = propertyRecord.TaxesAnnual / 12
    monthlyBreakdown.maintenance = propertyRecord.RepairCostsAnnual / 12

    monthlyBreakdown.profitExclMortgage = rent - monthlyBreakdown.hoa -  monthlyBreakdown.insurance - monthlyBreakdown.taxes - monthlyBreakdown.maintenance
}

function calculateMortgagePayment(propertyRecord) {
    let interestRate = propertyRecord.InterestRateAnnual / 100.0;

    if (interestRate > 0) {
        let loanAmount = propertyRecord.Price - propertyRecord.DownPayment;

        let monthlyRate = interestRate / 12.0;
        let termInMonths = 30.0 * 12.0;

        let monthlyPayment =
            (loanAmount * monthlyRate) /
            (1 - Math.pow(1+monthlyRate, -termInMonths));

        return monthlyPayment
    } else {
        return 0.0
    }

}

export function calculateProfitBreakdown(profitBreakdown, propertyRecord, year) {
    let sellingCost = propertyRecord.Price * .06;
    if (propertyRecord.DownPayment > 0) {
        let monthlyBreakdown = new MonthlyBreakdown();
        calculateMonthlyBreakdown(monthlyBreakdown, propertyRecord, year, false);
        const mortgagePayment = calculateMortgagePayment(propertyRecord);
        let monthlyProfit = monthlyBreakdown.profitExclMortgage - mortgagePayment;
        let profit = monthlyProfit * (year*11);
        let annualIncrease = propertyRecord.RentalAppreciationPctAnnual / 100;
        for (let i = 1; i < year; i++ ){            
            // Account for rental increases each year
            let increaseInThisYear = (propertyRecord.RentalPerMonth * (annualIncrease * year))
            profit = profit + (increaseInThisYear * 11)
        }

        let appreciation = (propertyRecord.Price * (propertyRecord.AppreciationPctAnnual / 100)) * year;        
        let otherExpenses = (monthlyBreakdown.monthlyRent - monthlyBreakdown.profitExclMortgage);
        let costOfVacancy = (mortgagePayment + otherExpenses) * year;

        let loanAmount = propertyRecord.Price - propertyRecord.DownPayment;
        let principalSavings = calculatePrincipalPayment(mortgagePayment,
            propertyRecord.InterestRateAnnual, loanAmount, year);
        let netProfit = profit - sellingCost - costOfVacancy + principalSavings;
        let roiPct = (netProfit / propertyRecord.DownPayment) * 100;
        let roiPctWithAppreciation = ((netProfit + appreciation) / propertyRecord.DownPayment) * 100;

        profitBreakdown.appreciation = appreciation;
        profitBreakdown.rentalIncome = profit;
        profitBreakdown.costOfVacancy = costOfVacancy;
        profitBreakdown.principalSavings = principalSavings;
        profitBreakdown.sellingCost = sellingCost;
        profitBreakdown.netProfit = netProfit;
        profitBreakdown.netProfitWithAppreciation = netProfit + appreciation;
        profitBreakdown.roiPct = Math.round(roiPct*10) / 10;
        profitBreakdown.roiPctWithAppreciation = Math.round(roiPctWithAppreciation*10) / 10;
    } else {
        profitBreakdown.roiPct = 0.0
    }
}

function calculateRoi(propertyRecord, mortgagePayment, withAppreciation) {    
    let roiMap = {};
    let currentYear = (new Date()).getFullYear();
    for (let i = 1; i <= 10; i++) {
        //Assuming that the home would be empty for one month a year.   
        if (propertyRecord.DownPayment > 0) {
            const profitBreakdown = new ProfitBreakdown();
            calculateProfitBreakdown(profitBreakdown, propertyRecord, i);
            if (withAppreciation) {
                roiMap[currentYear+i] = profitBreakdown.roiPctWithAppreciation;
            } else {
                roiMap[currentYear+i] = profitBreakdown.roiPct;
            }
        } else {
            roiMap[currentYear+i] = 0.0
        }

    }

    return roiMap
}

function calculateSTRRoi(propertyRecord, mortgagePayment, withAppreciation) {
    let roiMap = {};
    let currentYear = (new Date()).getFullYear();

    let sellingCost = propertyRecord.Price * .06;
    for (let i = 1; i <= 10; i++) {
        //Assuming that the home would be empty for one month a year.
        if (propertyRecord.DownPayment > 0) {
            let monthlyProfit = calculateShortTermRentalProfit(propertyRecord, i).profit - mortgagePayment;
            let profit = monthlyProfit * (i*11);
            let appreciation = 0.0;
            if (withAppreciation) {
                appreciation = (propertyRecord.Price *
                  (propertyRecord.AppreciationPctAnnual / 100)) * i;
            }
            let loanAmount = propertyRecord.Price - propertyRecord.DownPayment;
            let principalSavings = calculatePrincipalPayment(mortgagePayment,
              propertyRecord.InterestRateAnnual, loanAmount, i);
            let netProfit = profit - sellingCost + principalSavings;
            let roiPct = ((netProfit + appreciation) / propertyRecord.DownPayment) * 100;
            roiMap[currentYear+i] = Math.round(roiPct*10) / 10
        } else {
            roiMap[currentYear+i] = 0.0
        }

    }

    return roiMap
}

function calculatePrincipalPayment(monthlyPayment, interestRate, loanAmount, numberOfYears) {
    let principalSavings = 0.0;
    if (loanAmount > 0) {
        let loanBalance = loanAmount;
        for (let i = 1; i <= numberOfYears*12; i++) {
            let interestThisMonth = (loanBalance * ((interestRate / 100) / 12));
            let principalThisMonth = monthlyPayment - interestThisMonth;
            loanBalance = loanBalance - principalThisMonth;
            principalSavings += principalThisMonth
        }
    }

    return principalSavings
}
